import { useState } from 'react'
import { Link } from 'react-router-dom'
import SEOHead, { buildBreadcrumbs } from '../components/SEOHead'

const SITE_URL = 'https://nala.vet'

interface FAQ {
  question: string
  answer: string
  category: string
}

const FAQS: FAQ[] = [
  // ── About Nala ──────────────────────────────────────────────────────────
  {
    category: 'About Nala',
    question: 'What is Nala?',
    answer:
      'Nala is a mobile veterinary care service that sends licensed, background-checked veterinarians directly to your home. We provide the same quality care as a traditional clinic visit — without the stress of transporting your pet.',
  },
  {
    category: 'About Nala',
    question: 'What areas does Nala serve?',
    answer:
      'Nala currently operates in select metropolitan areas. Enter your zip code on our booking page to check availability in your area. We are continuously expanding to new cities.',
  },
  {
    category: 'About Nala',
    question: 'Are Nala veterinarians licensed?',
    answer:
      'Yes. All Nala veterinarians hold an active license issued by their state veterinary board, carry professional liability (malpractice) insurance, and have passed a thorough background check before joining our network.',
  },
  {
    category: 'About Nala',
    question: 'What species do you treat?',
    answer:
      'Our vets are experienced with dogs, cats, rabbits, guinea pigs, birds, reptiles, and other small animals. If you have an exotic species, contact us and we will confirm whether a specialist vet is available in your area.',
  },

  // ── Services ────────────────────────────────────────────────────────────────
  {
    category: 'Services',
    question: 'What services does Nala offer?',
    answer:
      'We offer wellness exams, vaccination visits, sick visits, puppy and kitten packages, senior pet wellness exams, and compassionate end-of-life care. Browse all available services on our booking page.',
  },
  {
    category: 'Services',
    question: 'Can Nala vets prescribe medications?',
    answer:
      'Yes. Our veterinarians are licensed to prescribe medications. Prescriptions can be sent electronically to your preferred pharmacy or dispensed directly at the time of the visit where permitted by state law.',
  },
  {
    category: 'Services',
    question: 'What if my pet needs surgery or emergency care?',
    answer:
      'Nala provides primary and preventive care as well as select diagnostics. For surgical procedures, advanced imaging, or true emergencies requiring hospitalization, our vet will provide a referral to the appropriate specialist or 24-hour emergency clinic.',
  },
  {
    category: 'Services',
    question: 'Can Nala perform blood work or run diagnostics?',
    answer:
      'Our vets carry portable diagnostic equipment for basic in-home testing. For comprehensive bloodwork, urinalysis, or imaging, samples are sent to a partner laboratory and results are typically available within 24–48 hours.',
  },

  // ── Appointments ────────────────────────────────────────────────────────────
  {
    category: 'Appointments & Booking',
    question: 'How do I book an appointment?',
    answer:
      'Create a free account at nala.vet/register, add your pet\'s information, then choose a service, date, time, and enter your home address. You will receive a confirmation email immediately.',
  },
  {
    category: 'Appointments & Booking',
    question: 'How much notice do I need to book?',
    answer:
      'Standard appointments are typically available within 24–72 hours. For urgent sick visits, same-day appointments are often available depending on vet availability in your area.',
  },
  {
    category: 'Appointments & Booking',
    question: 'Can I cancel or reschedule an appointment?',
    answer:
      'Yes. You may cancel or reschedule at no charge up to 24 hours before your appointment. Cancellations made less than 24 hours in advance may be subject to a late-cancellation fee.',
  },
  {
    category: 'Appointments & Booking',
    question: 'How do I prepare for a home vet visit?',
    answer:
      'Have your pet\'s medical records available if possible. Secure other pets in a separate room to reduce distraction. Have a quiet, well-lit area ready — indoors is preferred. That\'s it; we bring all the necessary equipment.',
  },

  // ── Pricing & Payment ───────────────────────────────────────────────────────
  {
    category: 'Pricing & Payment',
    question: 'How much does a home vet visit cost?',
    answer:
      'Pricing varies by service. Our wellness exams start at a competitive rate comparable to a traditional clinic visit, reflecting the added convenience of an in-home appointment. See the current pricing for each service on our booking page.',
  },
  {
    category: 'Pricing & Payment',
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit and debit cards, HSA and FSA cards, and CareCredit. Payment is collected securely through our app at the time of booking.',
  },
  {
    category: 'Pricing & Payment',
    question: 'Does Nala work with pet insurance?',
    answer:
      'Nala does not bill insurance directly. However, we provide a detailed invoice with procedure codes after each visit that you can submit to your pet insurance provider for reimbursement.',
  },

  // ── Safety ──────────────────────────────────────────────────────────────────
  {
    category: 'Safety',
    question: 'Is house-call veterinary care as safe and thorough as a clinic visit?',
    answer:
      'Yes. Our vets carry portable exam tables, stethoscopes, otoscopes, and other standard diagnostic tools. In some ways, a home visit provides a more accurate health picture because your pet is relaxed and in their natural environment — leading to lower stress readings and more typical behavior.',
  },
  {
    category: 'Safety',
    question: 'What COVID or infection-control precautions do Nala vets follow?',
    answer:
      'Our veterinarians follow current CDC and AVMA guidelines for infection control. This includes appropriate PPE, sanitizing equipment between visits, and following any household-specific requests you may have.',
  },
]

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
}

const categories = Array.from(new Set(FAQS.map((f) => f.category)))

function AccordionItem({ faq }: { faq: FAQ }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left py-5 flex items-center justify-between gap-4 hover:text-brand-700 transition-colors"
        aria-expanded={open}
      >
        <span className="font-medium text-gray-900">{faq.question}</span>
        <span className="text-xl text-gray-400 shrink-0">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <p className="pb-5 text-gray-600 leading-relaxed text-sm">{faq.answer}</p>
      )}
    </div>
  )
}

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const filtered = activeCategory
    ? FAQS.filter((f) => f.category === activeCategory)
    : FAQS

  return (
    <>
      <SEOHead
        title="Frequently Asked Questions"
        description="Answers to common questions about Nala in-home veterinary services — licensing, pricing, what to expect, booking, and more."
        canonical={`${SITE_URL}/faq`}
        jsonLd={[
          FAQ_JSON_LD,
          buildBreadcrumbs([{ name: 'FAQ', url: `${SITE_URL}/faq` }]),
        ]}
      />

      {/* Header */}
      <div className="bg-gradient-to-br from-brand-50 to-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-brand-600 font-semibold text-sm uppercase tracking-widest mb-3">
            Help Center
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-gray-600">
            Everything you need to know about Nala in-home veterinary care.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-16">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              !activeCategory
                ? 'bg-brand-700 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat === activeCategory ? null : cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === cat
                  ? 'bg-brand-700 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ accordion */}
        {categories
          .filter((cat) => !activeCategory || cat === activeCategory)
          .map((cat) => {
            const items = filtered.filter((f) => f.category === cat)
            if (!items.length) return null
            return (
              <div key={cat} className="mb-10">
                <h2 className="text-lg font-semibold text-gray-900 mb-2 pb-2 border-b border-gray-200">
                  {cat}
                </h2>
                <div>
                  {items.map((faq) => (
                    <AccordionItem key={faq.question} faq={faq} />
                  ))}
                </div>
              </div>
            )
          })}

        {/* CTA */}
        <div className="mt-16 card bg-brand-50 border-brand-100 text-center py-10">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Still have questions?</h2>
          <p className="text-gray-600 mb-6 text-sm">
            Our team is happy to help. Reach out at{' '}
            <a href="mailto:support@nala.vet" className="text-brand-700 font-semibold">
              support@nala.vet
            </a>
          </p>
          <Link to="/book" className="btn-primary">
            Book a Visit
          </Link>
        </div>
      </div>
    </>
  )
}
